import { KeyboardSensor, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { arrayMove, sortableKeyboardCoordinates } from '@dnd-kit/sortable';
import { useAddCaseFormContext } from '../contexts/AddCaseFormContext';
import { useParams } from 'react-router-dom';
import { useEffect, useRef, useState } from 'react';
import useCaseFieldUpdate from './useCaseFieldUpdate';
import { buildMedicalProviderPaths } from '../utils/caseFieldPaths';
import { generateDefaultProviderName, filterDuplicateFilesByName } from '../utils/helper';
import { UPDATE_CASE_ACTION } from '@demandproai/shared';
import {
    cloneRecords,
    getFileId,
    findDragIndex,
    findFileIndex,
    getNextIndex,
    extractFileFromProvider,
    updateSourceReorder,
    resolveFileDragProviders,
    insertFileIntoProvider,
} from '../utils/medicalReorderHelpers';

const useReorderMedicalProvider = ({ providerType }) => {
    const { formik, selectedTiffIndex, isShowReorderSec, toggleReorderSection } = useAddCaseFormContext();
    const { caseId } = useParams();

    const [recentlyMoved, setRecentlyMoved] = useState({ providerId: null, fileId: null });
    const [activeId, setActiveId] = useState(null);
    const [newProviderName, setNewProviderName] = useState('');
    const recentlyMovedTimeout = useRef(null);
    const snapshotRef = useRef(null);

    // capture a snapshot of records the moment the reorder panel opens
    useEffect(() => {
        if (isShowReorderSec[providerType]) {
            snapshotRef.current = cloneRecords(medicalRecords);
        }
    }, [isShowReorderSec[providerType]]);

    const formikValues = formik.values;
    const setFieldValue = formik.setFieldValue;

    const { updateCase } = useCaseFieldUpdate({ caseId });
    const medicalproviderPaths = buildMedicalProviderPaths({ selectedTiffIndex, providerType });

    const medicalRecords = formikValues?.clients[selectedTiffIndex]?.medicalProviders?.[providerType]?.medicalRecords;

    const buildMedicalProvidersUpdate = (updatedRecords) => {
        const currentProviders = formikValues?.clients?.[selectedTiffIndex]?.medicalProviders || {};
        const nextProviders = {
            ...currentProviders,
            postAccident: {
                ...(currentProviders?.postAccident || {}),
                medicalRecords: cloneRecords(currentProviders?.postAccident?.medicalRecords)
            },
            preAccident: {
                ...(currentProviders?.preAccident || {}),
                medicalRecords: cloneRecords(currentProviders?.preAccident?.medicalRecords)
            }
        };

        if (!nextProviders[providerType]) {
            nextProviders[providerType] = { medicalRecords: [] };
        }
        nextProviders[providerType].medicalRecords = cloneRecords(updatedRecords);
        return nextProviders;
    };

    const updateProviderData = (updatedRecords) => {
        setFieldValue(`clients[${selectedTiffIndex}].medicalProviders`, buildMedicalProvidersUpdate(updatedRecords));
    };

    const sensors = useSensors(
        useSensor(PointerSensor),
        useSensor(KeyboardSensor, {
            coordinateGetter: sortableKeyboardCoordinates
        })
    );

    const handleCancelReorder = () => {
        if (snapshotRef.current) {
            setFieldValue(`clients[${selectedTiffIndex}].medicalProviders`, buildMedicalProvidersUpdate(snapshotRef.current));
        }
        toggleReorderSection(providerType);
    };

    const handleSaveReorder = async () => {
        if (updateCase && medicalRecords) {
            await updateCase({ data: { [medicalproviderPaths.records]: medicalRecords }, action: UPDATE_CASE_ACTION.UPDATE });
        }
        toggleReorderSection(providerType);
    };

    const getProviderId = (_provider, fallbackIndex) => `${providerType}::provider-${fallbackIndex}`;

    const handleProviderReorder = async ({ active, over, medicalFiles }) => {
        const { oldIndex, newIndex } = findDragIndex(medicalFiles, getProviderId, active.id, over.id);
        if (oldIndex === -1 || newIndex === -1) return;
        const updated = arrayMove(medicalFiles, oldIndex, newIndex);
        updateProviderData(updated);
    };

    const handleFileReorderWithinProvider = async ({ active, over, medicalFiles, fromProviderId }) => {
        const { updated } = updateSourceReorder({
            medicalFiles,
            providerId: fromProviderId,
            getProviderId,
            getReorderedSource: (source) => {
                const { oldIndex, newIndex } = findDragIndex(source, (file, fi) => getFileId(fromProviderId, file, fi), active.id, over.id);
                if (oldIndex === -1 || newIndex === -1) return null;
                return arrayMove(source, oldIndex, newIndex);
            },
        });
        updateProviderData(updated);
    };

    const handleDragEnd = async (event) => {
        const medicalFiles = medicalRecords;
        const { active, over } = event;
        if (!over || active.id === over.id || !medicalFiles) return;

        const isProviderDrag = medicalFiles.some((p, i) => getProviderId(p, i) === active.id);
        if (isProviderDrag) {
            await handleProviderReorder({ active, over, medicalFiles });
            return;
        }

        const { fromProviderId, toProviderId } = resolveFileDragProviders({ medicalFiles, activeId: active.id, overId: over.id, getProviderId });

        if (!fromProviderId) return;

        if (!toProviderId || fromProviderId === toProviderId) {
            await handleFileReorderWithinProvider({ active, over, medicalFiles, fromProviderId });
            return;
        }

        await moveFileAcrossProviders({ fromProviderId, toProviderId, fileId: active.id });
    };

    const moveFileInProvider = async (providerId, fileId, direction) => {
        const medicalFiles = medicalRecords;
        if (!medicalFiles?.length) return false;

        const { updated, reorderedSource } = updateSourceReorder({
            medicalFiles,
            providerId,
            getProviderId,
            getReorderedSource: (source) => {
                const fileIndex = findFileIndex({ source, providerId, fileId });
                if (fileIndex === -1) return null;
                const nextIndex = getNextIndex({ fileIndex, direction, sourceLength: source.length });
                if (nextIndex === null) return null;
                return arrayMove(source, fileIndex, nextIndex);
            },
        });

        if (!reorderedSource) return false;

        updateProviderData(updated);
        return true;
    };

    // removes a file from its provider and creates a new provider with that file
    const addProviderWithFile = async ({ fileId, newProviderName }) => {
        const medicalFiles = medicalRecords;
        if (!medicalFiles?.length) return false;

        const { afterRemove, movedFile } = extractFileFromProvider({ medicalFiles, fileId, getProviderId });
        if (!movedFile) return false;

        const newProviderIndex = afterRemove.length;
        const clientName = formikValues?.clients?.[selectedTiffIndex]?.personalInfo?.clientName;
        const resolvedProviderName = newProviderName || generateDefaultProviderName(clientName, newProviderIndex);

        const finalProviders = [...afterRemove, { providerName: resolvedProviderName, source: [movedFile] }];
        updateProviderData(finalProviders);
        return true;
    };

    // moves a file from one provider into another provider's source list
    const moveFileAcrossProviders = async ({ toProviderId, fileId }) => {
        const medicalFiles = medicalRecords;
        if (!medicalFiles?.length) return false;

        const { afterRemove, movedFile } = extractFileFromProvider({ medicalFiles, fileId, getProviderId });
        if (!movedFile) return false;

        // block move if target provider already has a file with the same name
        const targetProvider = afterRemove.find((provider, index) => getProviderId(provider, index) === toProviderId);
        const uniqueFiles = filterDuplicateFilesByName({ newFiles: [movedFile], prevFiles: targetProvider?.source || [] });
        if (!uniqueFiles.length) return false;

        const { afterInsert } = insertFileIntoProvider({ medicalFiles: afterRemove, toProviderId, movedFile, getProviderId });
        updateProviderData(afterInsert);
        return true;
    };

    const NEW_PROVIDER_DROP_ID = `${providerType}::new-provider`;

    // finds the active dragged file and its index within its provider source
    const getActiveDragFile = () => {
        for (let i = 0; i < (medicalRecords || []).length; i++) {
            const provider = medicalRecords[i];
            const pid = getProviderId(provider, i);
            const fileIndex = provider.source?.findIndex((f, fi) => getFileId(pid, f, fi) === activeId);
            if (fileIndex !== -1 && fileIndex !== undefined) return { file: provider.source[fileIndex], fileIndex };
        }
        return null;
    };

    const onDragEnd = (event) => {
        setActiveId(null);
        if (event.over?.id === NEW_PROVIDER_DROP_ID) {
            addProviderWithFile({ fileId: event.active.id, newProviderName });
            setNewProviderName('');
        } else {
            handleDragEnd(event);
        }
    };

    const handleMoveWithFeedback = async (providerId, fileId, direction) => {
        const moved = await moveFileInProvider(providerId, fileId, direction);
        if (!moved) return;
        setRecentlyMoved({ providerId, fileId });
        if (recentlyMovedTimeout.current) clearTimeout(recentlyMovedTimeout.current);
        recentlyMovedTimeout.current = setTimeout(() => {
            setRecentlyMoved({ providerId: null, fileId: null });
        }, 400);
    };

    return {
        medicalRecords,
        isShowReorderSec,
        sensors,
        handleCancelReorder,
        handleSaveReorder,
        handleDragEnd,
        moveFileInProvider,
        moveFileAcrossProviders,
        addProviderWithFile,
        activeId,
        setActiveId,
        newProviderName,
        setNewProviderName,
        recentlyMoved,
        NEW_PROVIDER_DROP_ID,
        getProviderId,
        getActiveDragFile,
        onDragEnd,
        handleMoveWithFeedback,
    };
};

export default useReorderMedicalProvider;
